import React, { useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useDispatch, useSelector } from 'react-redux';

import { RootState, AppDispatch } from '@/store';
import { getCallPerformance } from '@/store/slices/callPerformanceSlice';
import { CallPerformancePeriod, CallScoreBand } from '@/types/api';
import { colors, font, space, radius, shadow } from '@/theme/chalkCourt';

const PERIODS: { key: CallPerformancePeriod; label: string }[] = [
  { key: 'THIS_WEEK', label: 'This Week' },
  { key: 'LAST_WEEK', label: 'Last Week' },
  { key: 'TWO_WEEKS_AGO', label: '2 Weeks Ago' },
  { key: 'THIS_MONTH', label: 'This Month' },
  { key: 'LAST_MONTH', label: 'Last Month' },
];

/** good → court green · okay → amber · low → coral. Matches the score bands. */
const bandColor = (band: CallScoreBand): string => {
  switch (band) {
    case 'good':
      return colors.court;
    case 'okay':
      return colors.warn;
    default:
      return colors.accent;
  }
};

/**
 * "My Call Performance" — the mobile port of iflipweb2's Vaadin tab: an AI
 * call-quality score card (score bar + Total / Avg Quality / Flagged /
 * Escalations + grade distribution + encouragement) and a "My Recent Calls"
 * list, filterable by period. Reached from the Dashboard.
 */
const CallPerformanceScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigation = useNavigation<NativeStackNavigationProp<any>>();
  const { data, isLoading, error, period } = useSelector(
    (state: RootState) => state.callPerformance
  );

  // Refetch on focus so the numbers are fresh whenever the screen is shown.
  // Deliberately keyed on `dispatch` only (not `period`) via a ref: the reducers
  // mutate `period`, so depending on it here would make every fetch re-trigger the
  // effect — a feedback loop that (with out-of-order responses) caused the period
  // pills to flip-flop. Period changes are driven solely by onSelectPeriod.
  const periodRef = useRef(period);
  periodRef.current = period;
  useFocusEffect(
    useCallback(() => {
      dispatch(getCallPerformance(periodRef.current));
    }, [dispatch])
  );

  const onSelectPeriod = (next: CallPerformancePeriod) => {
    if (next === period) {
      return; // already selected — ignore rapid re-taps of the active pill
    }
    dispatch(getCallPerformance(next));
  };

  const summary = data?.summary;
  const pct = summary?.avgQualityScorePct ?? 0;
  const scoreColor = bandColor(summary?.qualityBand ?? 'low');

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      refreshControl={
        <RefreshControl
          refreshing={isLoading}
          onRefresh={() => dispatch(getCallPerformance(period))}
          tintColor={colors.accent}
        />
      }>
      {/* Period selector */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.pillRow}
        contentContainerStyle={styles.pillRowContent}>
        {PERIODS.map((p) => {
          const active = p.key === period;
          return (
            <TouchableOpacity
              key={p.key}
              style={[styles.pill, active && styles.pillActive]}
              onPress={() => onSelectPeriod(p.key)}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}>
              <Text style={[styles.pillText, active && styles.pillTextActive]}>{p.label}</Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {isLoading && !data ? (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={colors.accent} />
        </View>
      ) : error ? (
        <View style={styles.centered}>
          <Text style={styles.stateTitle}>Couldn't load your performance</Text>
          <Text style={styles.stateBody}>{error}</Text>
          <TouchableOpacity
            style={styles.retryBtn}
            onPress={() => dispatch(getCallPerformance(period))}>
            <Text style={styles.retryText}>Try again</Text>
          </TouchableOpacity>
        </View>
      ) : data && !data.available ? (
        <View style={styles.centered}>
          <Text style={styles.stateTitle}>Call performance is unavailable</Text>
          <Text style={styles.stateBody}>
            Call scoring isn't reachable right now. Please try again later.
          </Text>
        </View>
      ) : data && !data.hasData ? (
        <View style={styles.centered}>
          <Text style={styles.stateTitle}>No scored calls</Text>
          <Text style={styles.stateBody}>
            You have no scored calls for {data.periodLabel.toLowerCase()}.
          </Text>
        </View>
      ) : summary ? (
        <>
          {/* Score card */}
          <View style={styles.card}>
            <Text style={styles.scoreLabel}>My Score</Text>
            <Text style={[styles.scoreValue, { color: scoreColor }]}>{pct}%</Text>
            <View style={styles.track}>
              <View
                style={[styles.fill, { width: `${Math.max(0, Math.min(100, pct))}%`, backgroundColor: scoreColor }]}
              />
            </View>

            <View style={styles.statGrid}>
              <Stat value={String(summary.totalCalls)} label="Total Calls" />
              <Stat value={`${pct}%`} label="Avg Quality" color={scoreColor} />
              <Stat
                value={String(summary.flaggedCount)}
                label="Flagged"
                color={summary.flaggedCount > 0 ? colors.warn : undefined}
              />
              <Stat
                value={String(summary.escalationCount)}
                label="Escalations"
                color={summary.escalationCount > 0 ? colors.accent : undefined}
              />
            </View>

            {summary.gradeDistribution.length > 0 && (
              <View style={styles.gradeRow}>
                {summary.gradeDistribution.map((g) => (
                  <View key={g.grade} style={styles.gradePill}>
                    <Text style={styles.gradePillText}>
                      {g.grade} · {g.count}
                    </Text>
                  </View>
                ))}
              </View>
            )}

            <Text style={styles.encouragement}>{summary.encouragement}</Text>
          </View>

          {/* Recent calls */}
          <Text style={styles.sectionTitle}>My Recent Calls</Text>
          {data && data.calls.length > 0 ? (
            data.calls.map((c) => {
              const rowColor = bandColor(c.scoreBand);
              return (
                <TouchableOpacity
                  key={c.callUuid}
                  style={styles.callCard}
                  activeOpacity={0.7}
                  onPress={() => navigation.navigate('CallDetail', { callUuid: c.callUuid })}>
                  <View style={styles.callHeader}>
                    <View style={styles.callHeaderLeft}>
                      <Text style={styles.callTime}>{c.startTimeDisplay || '—'}</Text>
                      {!!c.callType && (
                        <View style={styles.typeBadge}>
                          <Text style={styles.typeBadgeText}>{c.callType}</Text>
                        </View>
                      )}
                    </View>
                    <Text style={[styles.callScore, { color: rowColor }]}>
                      {c.scorePct}%{c.staffGrade ? ` ${c.staffGrade}` : ''}
                    </Text>
                  </View>
                  {!!c.managerNote && <Text style={styles.callNote}>{c.managerNote}</Text>}
                </TouchableOpacity>
              );
            })
          ) : (
            <Text style={styles.noCalls}>No recent calls found.</Text>
          )}
        </>
      ) : null}
    </ScrollView>
  );
};

const Stat: React.FC<{ value: string; label: string; color?: string }> = ({
  value,
  label,
  color,
}) => (
  <View style={styles.statCard}>
    <Text style={[styles.statValue, color ? { color } : null]}>{value}</Text>
    <Text style={styles.statLabel}>{label}</Text>
  </View>
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  content: {
    padding: space.lg,
    paddingBottom: space.xxl,
  },
  pillRow: {
    marginBottom: space.lg,
  },
  pillRowContent: {
    gap: space.sm,
    paddingRight: space.lg,
  },
  pill: {
    paddingHorizontal: space.md,
    paddingVertical: space.sm,
    borderRadius: radius.pill,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.line,
  },
  pillActive: {
    backgroundColor: colors.accent,
    borderColor: colors.accent,
  },
  pillText: {
    fontFamily: font.bodySemi,
    fontSize: 13,
    color: colors.inkSoft,
  },
  pillTextActive: {
    color: colors.onAccent,
  },
  centered: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: space.xxl,
    gap: space.sm,
  },
  stateTitle: {
    fontFamily: font.displayBold,
    fontSize: 20,
    color: colors.ink,
    textAlign: 'center',
  },
  stateBody: {
    fontFamily: font.body,
    fontSize: 15,
    color: colors.inkSoft,
    textAlign: 'center',
    paddingHorizontal: space.lg,
  },
  retryBtn: {
    marginTop: space.md,
    paddingHorizontal: space.xl,
    paddingVertical: space.sm,
    borderRadius: radius.pill,
    backgroundColor: colors.accent,
  },
  retryText: {
    fontFamily: font.bodyBold,
    color: colors.onAccent,
    fontSize: 15,
  },
  card: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    padding: space.xl,
    marginBottom: space.lg,
    ...shadow.card,
  },
  scoreLabel: {
    fontFamily: font.bodySemi,
    fontSize: 13,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: colors.inkFaint,
  },
  scoreValue: {
    fontFamily: font.display,
    fontSize: 56,
    lineHeight: 60,
    marginTop: space.xs,
  },
  track: {
    height: 10,
    borderRadius: radius.pill,
    backgroundColor: colors.line,
    overflow: 'hidden',
    marginTop: space.sm,
    marginBottom: space.lg,
  },
  fill: {
    height: '100%',
    borderRadius: radius.pill,
  },
  statGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  statCard: {
    width: '48%',
    backgroundColor: colors.surfaceSunk,
    borderRadius: radius.md,
    paddingVertical: space.md,
    paddingHorizontal: space.md,
    marginBottom: space.sm,
  },
  statValue: {
    fontFamily: font.displayBold,
    fontSize: 26,
    color: colors.ink,
  },
  statLabel: {
    fontFamily: font.bodyMed,
    fontSize: 13,
    color: colors.inkSoft,
    marginTop: 2,
  },
  gradeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: space.sm,
    marginTop: space.sm,
  },
  gradePill: {
    paddingHorizontal: space.md,
    paddingVertical: space.xs,
    borderRadius: radius.pill,
    backgroundColor: colors.courtSoft,
  },
  gradePillText: {
    fontFamily: font.bodySemi,
    fontSize: 13,
    color: colors.court,
  },
  encouragement: {
    fontFamily: font.bodyMed,
    fontSize: 15,
    color: colors.inkSoft,
    marginTop: space.lg,
  },
  sectionTitle: {
    fontFamily: font.displayBold,
    fontSize: 20,
    color: colors.ink,
    marginBottom: space.md,
  },
  callCard: {
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    padding: space.lg,
    marginBottom: space.sm,
    ...shadow.card,
  },
  callHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  callHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
    flex: 1,
  },
  callTime: {
    fontFamily: font.bodySemi,
    fontSize: 15,
    color: colors.ink,
  },
  typeBadge: {
    paddingHorizontal: space.sm,
    paddingVertical: 2,
    borderRadius: radius.sm,
    backgroundColor: colors.surfaceSunk,
    borderWidth: 1,
    borderColor: colors.line,
  },
  typeBadgeText: {
    fontFamily: font.bodyMed,
    fontSize: 12,
    color: colors.inkSoft,
  },
  callScore: {
    fontFamily: font.displayBold,
    fontSize: 18,
  },
  callNote: {
    fontFamily: font.body,
    fontSize: 14,
    color: colors.inkSoft,
    marginTop: space.xs,
  },
  noCalls: {
    fontFamily: font.body,
    fontSize: 15,
    color: colors.inkSoft,
    textAlign: 'center',
    paddingVertical: space.lg,
  },
});

export default CallPerformanceScreen;
